import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";

const BannerGallery = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const API_BASE_URL = 'https://api.tenarx.net';

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/banners`);
        const data = await res.json();
        if (data.success) {
          setBanners(data.data);
        } else {
          setError("Failed to load banners");
        }
      } catch (err) {
        setError("Failed to load banners");
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  // Auto-slide every 10 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  };

  if (loading) return <div className="py-4 text-center">Loading banners...</div>;
  if (error) return <div className="py-4 text-center text-red-500">{error}</div>;
  if (!banners.length) return <div className="py-4 text-center text-gray-500">No banners available.</div>;

  return (
    <div className="w-full relative">
      {/* Banner Container */}
      <div className="relative overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {banners.map((banner, index) => (
            <div 
              key={banner._id} 
              className="w-full flex-shrink-0"
            >
              <div className="relative">
                <img
                  src={banner.image.startsWith('http') ? banner.image : `${API_BASE_URL}${banner.image}`}
                  alt={banner.name}
                  className="w-full h-64 md:h-96 lg:h-[400px] object-cover"
                />
                {/* Banner Overlay with Title/Paragraph */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  {banner.overlayTitle && banner.overlayTitle.trim().length > 0 && (
                    <h3 className="text-white font-semibold text-lg">
                      {banner.overlayTitle}
                    </h3>
                  )}
                  {banner.overlayParagraph && banner.overlayParagraph.trim().length > 0 && (
                    <p className="text-white/90 text-sm mt-1 line-clamp-3">{banner.overlayParagraph}</p>
                  )}
                </div>
              </div>
          </div>
        ))}
        </div>

        {/* Navigation Arrows */}
        {banners.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Previous banner"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Next banner"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Navigation Dots */}
      {banners.length > 1 && (
        <div className="flex justify-center space-x-2 mt-4">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentSlide 
                  ? 'bg-blue-600 scale-125' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerGallery; 